import type { DashboardTimeSeriesSchema } from "@biteasy/contracts";
import { ResponsiveLine } from "@nivo/line";
import type { z } from "zod";

interface AttributionsChartProps {
	data: z.infer<typeof DashboardTimeSeriesSchema>;
}

/**
 * Line chart for attributions over time
 */
export function AttributionsChart({ data }: AttributionsChartProps) {
	const hasData = data.points.some((p) => p.value > 0);

	if (!hasData) {
		return (
			<div className="flex h-75 items-center justify-center rounded-xl border border-dashed border-base-content/20 bg-base-100/50">
				<p className="text-sm text-base-content/60">
					No attribution data available yet
				</p>
			</div>
		);
	}

	const chartData = [
		{
			id: "Attributions",
			color: "hsl(210, 70%, 50%)",
			data: data.points.map((p) => ({
				x: p.date,
				y: p.value,
			})),
		},
	];

	return (
		<div className="h-96 rounded-xl border border-base-content/10 bg-base-100 p-4">
			<ResponsiveLine
				data={chartData}
				enableSlices="x"
				sliceTooltip={({ slice }) => {
					return (
						<div className="min-w-40 rounded-lg border border-base-content/10 bg-base-100 p-3 shadow-xl">
							<div className="mb-2 text-xs font-semibold text-base-content/60">
								{new Date(slice.points[0].data.x as string).toLocaleDateString(
									undefined,
									{
										weekday: "short",
										year: "numeric",
										month: "short",
										day: "numeric",
									},
								)}
							</div>
							{slice.points.map((point) => (
								<div
									key={point.id}
									className="flex items-center justify-between gap-6 py-1"
								>
									<div className="flex items-center gap-2">
										<div
											className="size-3 rounded-full"
											style={{ backgroundColor: point.seriesColor }}
										/>
										<span className="text-sm font-medium text-base-content">
											{point.seriesId}
										</span>
									</div>
									<span className="text-sm font-bold text-base-content">
										{point.data.yFormatted}
									</span>
								</div>
							))}
						</div>
					);
				}}
				margin={{ top: 20, right: 20, bottom: 50, left: 50 }}
				xScale={{ type: "point" }}
				yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
				axisBottom={{
					tickSize: 5,
					tickPadding: 8,
					tickRotation: 0,
					tickValues: data.points
						.filter((_, i) => i % Math.ceil(data.points.length / 6) === 0)
						.map((p) => p.date),
					format: (v) => {
						const date = new Date(v);
						return `${date.getMonth() + 1}/${date.getDate()}`;
					},
				}}
				axisLeft={{
					tickSize: 5,
					tickPadding: 8,
					tickRotation: 0,
					format: (e) => (Number.isInteger(e) ? e : ""),
				}}
				enableGridX={false}
				gridYValues={5}
				pointSize={8}
				pointColor={{ theme: "background" }}
				pointBorderWidth={2}
				pointBorderColor={{ from: "seriesColor" }}
				enableArea={true}
				areaOpacity={0.1}
				useMesh={true}
				enableCrosshair={true}
				crosshairType="x"
				colors={["#3b82f6"]}
				theme={{
					background: "transparent",
					text: {
						fill: "var(--color-base-content)",
						fontSize: 11,
					},
					axis: {
						domain: {
							line: {
								stroke: "var(--color-base-content)",
								strokeOpacity: 0.2,
								strokeWidth: 1,
							},
						},
						ticks: {
							line: {
								stroke: "var(--color-base-content)",
								strokeOpacity: 0.2,
								strokeWidth: 1,
							},
							text: {
								fill: "var(--color-base-content)",
								fillOpacity: 0.6,
							},
						},
					},
					grid: {
						line: {
							stroke: "var(--color-base-content)",
							strokeOpacity: 0.08,
							strokeDasharray: "4 4",
						},
					},
					crosshair: {
						line: {
							stroke: "var(--color-base-content)",
							strokeOpacity: 0.3,
							strokeWidth: 1,
							strokeDasharray: "4 4",
						},
					},
				}}
			/>
		</div>
	);
}
